import { useMemo } from 'react';
import { typography, radius, semantic } from '@/shared/theme/tokens';
import { LIVE_STATUS_META, valueBounds, withAlpha } from '../../liveModeMeta';
import { Sparkline } from '../../ui/Sparkline';
import { MONO, arcPath, dialPoint, clamp01, GAUGE_START, GAUGE_SWEEP } from './recommendedTheme';
import { FlipNumber } from './FlipNumber';

const CX = 60;
const CY = 60;
const R = 46;
const SW = 7;
const MAX = 6;

function pickGauges(alerts) {
  const byName = new Map();
  for (const a of alerts) if (!byName.has(a.parameterName)) byName.set(a.parameterName, a);
  return [...byName.values()].slice(0, MAX);
}

/**
 * Parameter Telemetry — the HUD arc-gauge rail, theme-aware, with Board-style
 * flip numerals at the dial centre (the readout flips when the value changes).
 * Full-width strip below the work-queue. Read-only. Stale rings grey out.
 */
export function TelemetryRail({ alerts, flashId, animate, c }) {
  const gauges = useMemo(() => pickGauges(alerts), [alerts]);
  if (gauges.length === 0) return null;

  return (
    <section style={{
      flexShrink: 0, background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        padding: '9px 16px', borderBottom: `1px solid ${c.border}`,
      }}>
        <span style={{
          fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold,
          color: c.textPrimary, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
        }}>
          Parameter Telemetry
        </span>
        <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide }}>
          VALUE · BAND · TREND
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
        {gauges.map((a, i) => (
          <Gauge key={a.id} alert={a} flash={flashId === a.id} animate={animate} c={c}
            divider={i > 0} />
        ))}
      </div>
    </section>
  );
}

function Gauge({ alert, flash, animate, c, divider }) {
  const stale = !!alert.isStale;
  const meta = LIVE_STATUS_META[alert.status] ?? LIVE_STATUS_META.ok;
  const color = stale ? c.textSubtle : meta.color;

  const { lo, hi } = valueBounds(alert);
  const span = (hi - lo) || 1;
  const f = clamp01((alert.liveValue - lo) / span);
  const hasBand = alert.optimalMin != null && alert.optimalMax != null;
  const bf0 = hasBand ? clamp01((alert.optimalMin - lo) / span) : 0;
  const bf1 = hasBand ? clamp01((alert.optimalMax - lo) / span) : 0;
  const dot = dialPoint(CX, CY, R, f);

  return (
    <div style={{
      flex: '1 1 150px', minWidth: 150, display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 3, padding: '12px 10px 12px', borderLeft: divider ? `1px solid ${c.border}` : 'none',
      background: flash && !stale ? withAlpha(color, '14') : 'transparent',
      transition: animate ? 'background 0.5s ease' : 'none', opacity: stale ? 0.6 : 1,
    }}>
      <div style={{ position: 'relative', width: 120, height: 92 }}>
        <svg viewBox="0 0 120 108" width="120" height="92" style={{ display: 'block', overflow: 'visible' }}>
          <path d={arcPath(CX, CY, R, GAUGE_START, GAUGE_START + GAUGE_SWEEP)} fill="none" stroke={c.bgMuted} strokeWidth={SW} strokeLinecap="round" />
          {hasBand && bf1 > bf0 && (
            <path d={arcPath(CX, CY, R, GAUGE_START + bf0 * GAUGE_SWEEP, GAUGE_START + bf1 * GAUGE_SWEEP)}
              fill="none" stroke={withAlpha(semantic.successAlt, '4d')} strokeWidth={SW} strokeLinecap="butt" />
          )}
          {!stale && f > 0.001 && (
            <path d={arcPath(CX, CY, R, GAUGE_START, GAUGE_START + f * GAUGE_SWEEP)} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round" />
          )}
          {!stale && <circle cx={dot.x} cy={dot.y} r={4} fill={c.bg} stroke={color} strokeWidth={2} />}
        </svg>
        <div style={{ position: 'absolute', inset: 0, top: -6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          {stale ? (
            <span style={{ fontFamily: MONO, fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: c.textSubtle }}>——</span>
          ) : (
            <FlipNumber value={alert.liveValue} color={color} c={c} size="sm" animate={animate && flash} />
          )}
          {alert.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, marginTop: 3 }}>{alert.unit}</span>}
        </div>
      </div>
      <div style={{
        fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textMuted, letterSpacing: typography.letterSpacing.wide,
        textTransform: 'uppercase', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center',
      }}>
        {alert.parameterName}
      </div>
      <div style={{ width: '78%', height: 16, opacity: stale ? 0.4 : 0.9 }}>
        <Sparkline data={alert.buffer} color={color} height={16} animate={false} />
      </div>
    </div>
  );
}
